import { Box, Button, Typography } from "@mui/material"
import { DataGrid, GridColDef, GridRowParams, GridToolbar } from "@mui/x-data-grid"
import { StyledButton } from "components/Button/StyledButton"
import { HaufeLoader } from "components/HaufeLoader/HaufeLoader"
import { Lead, Project } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AppRoutes } from "store/routes"

const Projects = () => {
  const { projects, leads, customers } = useStoreState((state) => state)
  const { fetchProjects, fetchLeads, fetchCustomers } = useStoreActions((actions) => actions)
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)
        await Promise.all([
          fetchProjects({ fresh: true }),
          fetchLeads({ fresh: false }),
          fetchCustomers({ fresh: false }),
        ])
      } catch (err) {
        console.error("Error loading data:", err)
        const msg = (err as Error)?.message || "Unbekannter Fehler"
        setError(msg)
        enqueueSnackbar("Fehler beim Laden der Projektdaten: " + msg, {
          variant: "error",
        })
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [fetchProjects, fetchLeads, fetchCustomers])

  const handleRetry = async () => {
    try {
      setError(null)
      setLoading(true)
      await Promise.all([
        fetchProjects({ fresh: true }),
        fetchLeads({ fresh: false }),
        fetchCustomers({ fresh: false }),
      ])
    } catch (err) {
      console.error("Error reloading project data:", err)
      const msg = (err as Error)?.message || "Unbekannter Fehler"
      setError(msg)
      enqueueSnackbar("Fehler beim Laden der Projektdaten: " + msg, {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const getCustomerName = (opportunityID: string) => {
    const lead = leads.find((lead) => lead.opportunityID === opportunityID)
    if (lead) {
      const customer = customers.find((customer) => customer.customerID === lead.businessAccount)
      if (customer) {
        return customer.customerName
      }
    }
    return ""
  }

  const columns: GridColDef<Project>[] = [
    {
      field: "id",
      headerName: "ID",
      flex: 1,

      valueGetter: (params: string) => {
        const opID =
          projects.find((project) => project.id === params)?.lead?.opportunityID || params
        return opID
      },
    },

    {
      field: "name",
      headerName: "Name",
      flex: 4,
    },
    {
      field: "lead",
      headerName: "Firma",
      flex: 4,
      valueGetter: (params: Lead) => {
        return getCustomerName(params.opportunityID || "")
      },
    },
    {
      field: "actions",
      type: "actions",

      getActions: (params: GridRowParams) => [
        <StyledButton
          buttonType="edit"
          iconOnly={true}
          onClick={() =>
            navigate(`${AppRoutes.PROJECTS}/${params.row.lead?.opportunityID || params.row.id}`)
          }
          size="small"
        />,
      ],
      flex: 1,
    },
  ]

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <HaufeLoader message="Lade Projektdaten..." />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography variant="h6" color="error">
          Fehler beim Laden der Projektdaten
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
        <Button variant="contained" onClick={handleRetry}>
          Erneut versuchen
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <DataGrid
        rows={projects.filter((project) => project && project.id)}
        columns={columns}
        getRowId={(row) => row.id}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        initialState={{
          sorting: { sortModel: [{ field: "id", sort: "desc" }] },
        }}
      />
    </Box>
  )
}

export default Projects
